/**
 * main-pod-internal-dashboard.js
 * 内部设计师 Dashboard - 订单列表 + 校稿上传
 * R2 上传逻辑复用自 main-pod-upload-page.liquid
 */

class PODInternalDashboard {
  constructor(container) {
    this.container = container;
    this.orders = [];
    this.currentFilter = 'all';
    this.currentScope = 'active';   // 'active' | 'all'
    this.currentSort = 'newest';  // 'newest' | 'oldest' | 'name-asc' | 'name-desc'
    this.searchQuery = '';
    this.selectedOrder = null;
    this.uploadedProofFile = null;
    /* 分页状态 */
    this.pageLimit = 50;          // 每页订单数
    this.pageStack = [];          // 历史页的 since_id 栈, 用于"上一页"
    this.currentSinceId = 0;      // 当前页起始 since_id (0 = 首页)
    this.hasNext = false;         // 是否还有下一页
    this.isLoadingPage = false;   // 防止翻页时重复请求
    /* 时间范围: days_back. 0 = 全部, 1 = 今天, 7 = 近7天, 30 = 近30天, 90 = 近90天 */
    this.daysBack = 30;
    this.uploadedProofUrl = null;  // 上传到 R2 后的公开 URL（两步流程用）
    this.uploadedProofR2Key = null; // R2 key（用于撤销删除）
    this.uploadEndpoint = '';  // R2 Worker endpoint
    this.maxFileMB = 10;

    this.init();
  }

  async init() {
    // 从 section settings 读取配置
    const section = document.querySelector('[data-section-type="pod-internal-dashboard"]');
    if (section) {
      this.uploadEndpoint = section.dataset.podWorkerEndpoint || window.POD_WORKER_ENDPOINT || '';
      this.maxFileMB = parseInt(section.dataset.podMaxFileMb) || 10;
    }

    // fallback 到全局配置
    if (!this.uploadEndpoint && typeof window !== 'undefined' && window.POD_WORKER_ENDPOINT) {
      this.uploadEndpoint = window.POD_WORKER_ENDPOINT;
    }

    // 关键修复: 让 PODOrderAPI 从 window.POD_CONFIG[*] 读取 hmacSecret (DSN-1)
    if (typeof PODOrderAPI !== 'undefined' && typeof PODOrderAPI.init === 'function') {
      PODOrderAPI.init({ workerEndpoint: this.uploadEndpoint, maxFileMB: this.maxFileMB });
    }

    /* Bug 1 Fix (20260727-2): render() 必须先于 bindEvents() 执行,
     * 因为 bindEvents() 直接绑定搜索/排序等 DOM 元素的监听器,
     * 若 DOM 尚未由 render() 生成,这些监听器会指向不存在的元素而失效。*/
    await this.loadOrders();
    this.render();
    this.bindEvents();
  }

  bindEvents() {
    // 筛选按钮
    this.container.addEventListener('click', (e) => {
      const filterBtn = e.target.closest('.pod-filter-btn');
      if (filterBtn) {
        this.currentFilter = filterBtn.dataset.filter;
        this.renderOrders();
        this.updateFilterButtons();
      }

      const scopeBtn = e.target.closest('.pod-scope-btn');
      if (scopeBtn) {
        this.currentScope = scopeBtn.dataset.scope;
        this.render();
      }
    });

    // 搜索输入
    const searchInput = this.container.querySelector('.pod-search-input');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => {
        this.searchQuery = e.target.value.toLowerCase();
        this.renderOrders();
      });
    }

    // 排序选择
    const sortSelect = this.container.querySelector('.pod-sort-select');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        this.currentSort = e.target.value;
        this.renderOrders();
      });
    }

    // 时间范围选择
    const daysBackSelect = this.container.querySelector('.pod-days-back-select');
    if (daysBackSelect) {
      daysBackSelect.addEventListener('change', (e) => {
        this.daysBack = parseInt(e.target.value, 10) || 30;
        /* 切换时间范围时重置分页, 重新拉取 */
        this.resetPagination();
        this.loadOrders().then(() => this.render());
      });
    }

    // 表格行点击
    this.container.addEventListener('click', (e) => {
      const row = e.target.closest('[data-order-id]');
      if (row) {
        this.openOrderModal(row.dataset.orderId);
      }
    });

    /* 翻页按钮 */
    this.container.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('.pod-page-prev');
      const nextBtn = e.target.closest('.pod-page-next');
      if (prevBtn) { e.preventDefault(); this.goToPrevPage(); }
      if (nextBtn) { e.preventDefault(); this.goToNextPage(); }
    });

    // 模态框关闭
    this.container.addEventListener('click', (e) => {
      if (e.target.closest('.pod-modal__close') || e.target.classList.contains('pod-modal-overlay')) {
        this.closeModal();
      }
    });

    // 上传区域
    this.bindUploadEvents();
  }

  bindUploadEvents() {
    const modal = this.container.querySelector('.pod-modal');
    if (!modal) return;

    const uploadZone = modal.querySelector('.pod-upload-zone');
    const fileInput = modal.querySelector('.pod-upload-input');
    const removeBtn = modal.querySelector('.pod-upload-preview__remove');
    const sendBtn = modal.querySelector('.pod-send-proof-btn');

    if (uploadZone && fileInput) {
      uploadZone.addEventListener('click', () => fileInput.click());

      uploadZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        uploadZone.classList.add('pod-upload-zone--dragover');
      });

      uploadZone.addEventListener('dragleave', () => {
        uploadZone.classList.remove('pod-upload-zone--dragover');
      });

      uploadZone.addEventListener('drop', (e) => {
        e.preventDefault();
        uploadZone.classList.remove('pod-upload-zone--dragover');
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.handleFileSelect(files[0]);
        }
      });

      fileInput.addEventListener('change', (e) => {
        if (e.target.files.length > 0) {
          this.handleFileSelect(e.target.files[0]);
        }
      });
    }

    if (removeBtn) {
      removeBtn.addEventListener('click', async () => {
        const r2Key = this.uploadedProofR2Key;
        const orderId = this.selectedOrder ? this.selectedOrder.id : '';

        // 先调用 Worker DELETE API 清理 R2 文件（不阻塞 UI）
        if (r2Key && this.uploadEndpoint) {
          try {
            await PODOrderAPI.deleteFile(r2Key);
          } catch (e) {
            console.warn('[POD Dashboard] Failed to delete R2 file:', r2Key, e.message);
          }
        }

        this.uploadedProofFile = null;
        this.uploadedProofUrl = null;
        this.uploadedProofR2Key = null;
        if (orderId) { try { localStorage.removeItem('pod_proof_' + orderId); } catch (e) {} }
        this.updateUploadPreview();
        // 重置发送按钮
        const sendBtn = this.container.querySelector('.pod-send-proof-btn');
        if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = '发送给客户（请先上传）'; }
      });
    }

    if (sendBtn) {
      sendBtn.addEventListener('click', () => this.sendProof());
    }

    // 两步流程：上传到服务器按钮
    const uploadNowBtn = modal.querySelector('.pod-upload-now-btn');
    if (uploadNowBtn) {
      uploadNowBtn.addEventListener('click', () => this.uploadProofNow());
    }

    // Bug 5 Fix (20260727-2): 复制图片链接按钮(事件委托)
    modal.querySelectorAll('[data-copy-url]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-copy-url');
        navigator.clipboard.writeText(url).then(() => {
          const orig = btn.textContent;
          btn.textContent = '已复制';
          btn.disabled = true;
          setTimeout(() => {
            btn.textContent = orig;
            btn.disabled = false;
          }, 1500);
        });
      });
    });

    // 20260726-1 BUG-6 修复: "标记进入生产" 按钮 handler
    // 调用 PODOrderAPI.updateOrder(orderId, { '_proof_status': 'in-production' }),
    // 后端 pod-internal-api.js handleOrderUpdate 推 pod_in_production event 给 Klaviyo (F-009 → EM-006)
    const pushBtn = modal.querySelector('.pod-push-production-btn');
    if (pushBtn) {
      pushBtn.addEventListener('click', () => this.pushOrderToProduction(pushBtn));
    }

    // 客户图 × 按钮（设计师删除客户上传的图，仅 awaiting-art 状态可见）
    modal.querySelectorAll('[data-pod-customer-image-remove]').forEach((btn) => {
      btn.addEventListener('click', () => this.handleRemoveCustomerImage(btn));
    });
  }

  handleFileSelect(file) {
    // 验证文件类型：图片、GIF、HEIC/HEIF、视频、图片 PDF
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/webp', 'image/gif',
      'image/heic', 'image/heif',
      'application/pdf',
      'video/mp4', 'video/quicktime', 'video/webm', 'video/mov'
    ];
    // HEIC/HEIF 在某些浏览器用大写 MIME
    const normalizedType = file.type.toLowerCase();
    const heicVariants = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
    const isAllowed = allowedTypes.includes(normalizedType) || heicVariants.includes(normalizedType);
    if (!isAllowed) {
      this.showToast('请上传图片（JPG、PNG、WebP、GIF、HEIC/HEIF）、视频（MP4、MOV）或 PDF', 'error');
      return;
    }

    this.uploadedProofFile = file;
    this.updateUploadPreview();
  }

  updateUploadPreview() {
    const modal = this.container.querySelector('.pod-modal');
    if (!modal) return;

    const uploadZone = modal.querySelector('.pod-upload-zone');
    const previewContainer = modal.querySelector('.pod-upload-preview-container');

    if (this.uploadedProofFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (uploadZone) uploadZone.style.display = 'none';
        if (previewContainer) {
          previewContainer.style.display = 'block';
          const imgEl = previewContainer.querySelector('[data-preview-img]');
          const videoEl = previewContainer.querySelector('[data-preview-video]');
          const isVideo = /^video\//.test(this.uploadedProofFile.type);
          if (isVideo) {
            if (imgEl) { imgEl.style.display = 'none'; imgEl.src = ''; }
            if (videoEl) { videoEl.style.display = 'block'; videoEl.src = e.target.result; }
          } else {
            if (videoEl) { videoEl.style.display = 'none'; videoEl.src = ''; }
            if (imgEl) { imgEl.style.display = 'block'; imgEl.src = e.target.result; imgEl.alt = '校稿预览'; }
          }
          // 选中文件后，启用"上传到服务器"按钮，同时禁用"发送给客户"按钮
          const uploadBtn = previewContainer.querySelector('.pod-upload-now-btn');
          if (uploadBtn) { uploadBtn.disabled = false; uploadBtn.textContent = '上传到服务器'; }
          const sendBtn = this.container.querySelector('.pod-send-proof-btn');
          if (sendBtn) { sendBtn.disabled = true; sendBtn.textContent = '发送给客户（请先上传）'; }
        }
      };
      reader.readAsDataURL(this.uploadedProofFile);
    } else {
      if (uploadZone) uploadZone.style.display = 'block';
      if (previewContainer) previewContainer.style.display = 'none';
    }
  }

  async loadOrders() {
    // 关键修复 (P0-5): 调用真实 Worker API, mock 作为 fallback
    if (!this.uploadEndpoint) {
      this.orders = [];
      this._loadError = 'Worker endpoint not configured. Set this in section settings.';
      return;
    }

    if (!window.PODOrderAPI) {
      console.error('[POD Dashboard] PODOrderAPI not loaded');
      this.orders = [];
      this._loadError = 'PODOrderAPI 脚本未加载。';
      return;
    }

    /* 复用 PODOrderAPI.callWorker (HMAC 签名由它自动处理).
     * Worker 端 verifyInternalSignature 兼容空 orderId 签名. */
    this.isLoadingPage = true;
    this.renderOrders();
    try {
      const qs = new URLSearchParams({
        status: 'pending',
        limit: String(this.pageLimit),
      });
      if (this.currentSinceId > 0) qs.set('since_id', String(this.currentSinceId));
      if (this.daysBack > 0) qs.set('days_back', String(this.daysBack));
      const data = await PODOrderAPI.callWorker('/orders/list?' + qs.toString(), {}, 'GET');
      if (data && Array.isArray(data.orders)) {
        this.orders = data.orders;
        const page = data.page || {};
        this.hasNext = !!page.hasNext;
        this._nextSinceId = page.nextSinceId || null;
        return;
      }
      console.warn('[POD Dashboard] /orders/list returned no orders array, falling back to empty');
    } catch (error) {
      console.error('[POD Dashboard] /orders/list failed:', error);
      this._loadError = '加载订单失败。已切换为空状态。';
    } finally {
      this.isLoadingPage = false;
    }
    this.orders = [];
  }

  goToNextPage() {
    if (!this.hasNext || !this._nextSinceId || this.isLoadingPage) return;
    // 记录当前页的 since_id 到栈, 用于"上一页"
    this.pageStack.push(this.currentSinceId || 0);
    this.currentSinceId = this._nextSinceId;
    this.loadOrders().then(() => this.renderOrders());
  }

  goToPrevPage() {
    if (this.pageStack.length === 0 || this.isLoadingPage) return;
    this.currentSinceId = this.pageStack.pop();
    this.loadOrders().then(() => this.renderOrders());
  }

  resetPagination() {
    this.pageStack = [];
    this.currentSinceId = 0;
    this.hasNext = false;
    this._nextSinceId = null;
  }

  getMockOrders() {
    return [
      {
        id: '12345',
        name: '#1001',
        customer: {
          name: '张三',
          email: 'zhangsan@example.com'
        },
        product: '定制照片 T 恤',
        uploadMode: 'SendLater',
        image1: 'https://picsum.photos/400/400?random=1',
        image2: 'https://picsum.photos/400/400?random=2',
        image3: '', image4: '', image5: '',
        status: 'pending',
        proofSentAt: '',
        tags: 'pod-order,awaiting-art',
        created_at: '2026-07-15T10:30:00Z'
      },
      {
        id: '12346',
        name: '#1002',
        customer: {
          name: '李四',
          email: 'lisi@example.com'
        },
        product: '定制手机壳',
        uploadMode: 'SendLater',
        image1: '', image2: '',
        image3: '', image4: '', image5: '',
        status: 'pending',
        proofSentAt: '',
        tags: 'pod-order,awaiting-photo',
        created_at: '2026-07-15T14:20:00Z'
      },
      {
        id: '12347',
        name: '#1003',
        customer: {
          name: '王五',
          email: 'wangwu@example.com'
        },
        product: '定制照片马克杯',
        uploadMode: 'Submitted',
        image1: 'https://picsum.photos/400/400?random=4',
        image2: 'https://picsum.photos/400/400?random=5',
        image3: '', image4: '', image5: '',
        status: 'proof-sent',
        proofSentAt: '2026-07-14T09:00:00Z',
        tags: 'pod-order,awaiting-art',
        created_at: '2026-07-13T11:00:00Z'
      },
      {
        id: '12348',
        name: '#1004',
        customer: {
          name: '赵六',
          email: 'zhaoliu@example.com'
        },
        product: '定制照片画布',
        uploadMode: 'Submitted',
        image1: 'https://picsum.photos/400/400?random=6',
        image2: '',
        image3: '', image4: '', image5: '',
        status: 'proof-rejected',
        proofSentAt: '2026-07-13T10:00:00Z',
        tags: 'pod-order,awaiting-art',
        created_at: '2026-07-12T16:00:00Z'
      },
      {
        id: '12349',
        name: '#1005',
        customer: {
          name: '钱七',
          email: 'qianqi@example.com'
        },
        product: '定制照片抱枕',
        uploadMode: 'Submitted',
        image1: 'https://picsum.photos/400/400?random=7',
        image2: 'https://picsum.photos/400/400?random=8',
        image3: '', image4: '', image5: '',
        status: 'proof-approved',
        proofSentAt: '2026-07-14T10:00:00Z',
        tags: 'pod-order',
        created_at: '2026-07-13T09:00:00Z'
      }
    ];
  }

  getFilteredOrders() {
    let filtered = [...this.orders];

    // 状态筛选
    if (this.currentFilter === 'awaiting-photo') {
      filtered = filtered.filter(o => o.uploadMode === 'SendLater' && !o.proofSentAt);
    } else if (this.currentFilter === 'awaiting-art') {
      filtered = filtered.filter(o => {
        const tags = o.tags || '';
        return tags.includes('awaiting-art');
      });
    } else if (this.currentFilter === 'proof-approved') {
      filtered = filtered.filter(o =>
        ['proof-approved', 'auto-approved'].includes(o.status)
      );
    } else if (this.currentFilter === 'in-production') {
      filtered = filtered.filter(o => o.status === 'in-production');
    } else if (this.currentFilter === 'completed') {
      filtered = filtered.filter(o =>
        o.status === 'in-production' && o.fulfillmentStatus === 'fulfilled'
      );
    } else if (this.currentFilter !== 'all') {
      filtered = filtered.filter(order => order.status === this.currentFilter);
    }

    // 未完成 / 全量筛选（仅对 'all' 生效）
    if (this.currentScope === 'active' && this.currentFilter === 'all') {
      filtered = filtered.filter(o =>
        o.status !== 'in-production' ||
        (o.status === 'in-production' && o.fulfillmentStatus === 'fulfilled')
      );
    }

    // 搜索筛选
    if (this.searchQuery) {
      filtered = filtered.filter(order =>
        order.name.toLowerCase().includes(this.searchQuery) ||
        order.customer.name.toLowerCase().includes(this.searchQuery) ||
        order.customer.email.toLowerCase().includes(this.searchQuery)
      );
    }

    // 排序
    filtered.sort((a, b) => {
      switch (this.currentSort) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name-asc':
          return a.name.localeCompare(b.name, 'zh-CN', { numeric: true });
        case 'name-desc':
          return b.name.localeCompare(a.name, 'zh-CN', { numeric: true });
        default:
          return 0;
      }
    });

    return filtered;
  }

  getStats() {
    const isActive = o => o.status !== 'in-production';
    const all = this.orders;
    const active = all.filter(isActive);
    return {
      awaitingPhoto: (this.currentScope === 'active' ? active : all)
        .filter(o => o.uploadMode === 'SendLater' && !o.proofSentAt).length,
      awaitingArt: (this.currentScope === 'active' ? active : all)
        .filter(o => o.status === 'awaiting-art').length,
      proofSent: (this.currentScope === 'active' ? active : all)
        .filter(o => o.status === 'proof-sent').length,
      approved: (this.currentScope === 'active' ? active : all)
        .filter(o => ['proof-approved', 'auto-approved'].includes(o.status)).length,
      rejected: (this.currentScope === 'active' ? active : all)
        .filter(o => o.status === 'proof-rejected').length,
      completed: all.filter(o =>
        o.status === 'in-production' && o.fulfillmentStatus === 'fulfilled'
      ).length,
      total: all.length,
      activeTotal: active.length
    };
  }

  renderProofMedia(url) {
    if (!url) return '';
    const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
    const isVideo = /\.(mp4|mov|webm|avi)$/i.test(url);
    if (isVideo) {
      return `<video src="${url}" controls style="width:100%;max-height:400px;display:block;"></video>`;
    }
    if (isImage) {
      return `<img src="${url}" alt="已发送的校稿文件" style="width:100%;max-height:400px;object-fit:contain;display:block;">`;
    }
    return `<a href="${url}" target="_blank" class="pod-proof-download-link">下载文件</a>`;
  }

  formatTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} 天前`;
    } else if (diffHours > 0) {
      return `${diffHours} 小时前`;
    } else {
      return '刚刚';
    }
  }

  getTimeRemaining(dateString, hours = 72) {
    const sentDate = new Date(dateString);
    const deadline = new Date(sentDate.getTime() + hours * 60 * 60 * 1000);
    const now = new Date();
    const remainingMs = deadline - now;

    if (remainingMs <= 0) {
      return '已超时';
    }

    const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
    const remainingMinutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

    if (remainingHours > 24) {
      const days = Math.floor(remainingHours / 24);
      return `剩余 ${days} 天 ${remainingHours % 24} 小时`;
    }
    return `剩余 ${remainingHours} 小时 ${remainingMinutes} 分钟`;
  }

  render() {
    const stats = this.getStats();
    const filteredOrders = this.getFilteredOrders();

    this.container.innerHTML = `
      <div class="pod-internal-dashboard">
        <!-- Header -->
        <header class="pod-internal-dashboard__header">
          <div>
            <h1 class="pod-internal-dashboard__title">校稿管理</h1>
            <p class="pod-internal-dashboard__subtitle">内部团队管理客户校稿请求的工作台</p>
          </div>
        </header>

        <!-- Stats -->
        <div class="pod-internal-dashboard__stats">
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--awaiting-photo">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.awaitingPhoto}</div>
              <div class="pod-stat-card__label">待确认图片</div>
            </div>
          </div>
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--awaiting">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.awaitingArt}</div>
              <div class="pod-stat-card__label">待制作</div>
            </div>
          </div>
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--sent">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 2L11 13"/>
                <path d="M22 2l-7 20-4-9-9-4 20-7z"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.proofSent}</div>
              <div class="pod-stat-card__label">已发送校稿</div>
            </div>
          </div>
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--approved">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <path d="M22 4L12 14.01l-3-3"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.approved}</div>
              <div class="pod-stat-card__label">已确认</div>
            </div>
          </div>
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--rejected">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6"/>
                <path d="M9 9l6 6"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.rejected}</div>
              <div class="pod-stat-card__label">需修改</div>
            </div>
          </div>
          <div class="pod-stat-card">
            <div class="pod-stat-card__icon pod-stat-card__icon--completed">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="pod-stat-card__content">
              <div class="pod-stat-card__value">${stats.completed}</div>
              <div class="pod-stat-card__label">已完成</div>
            </div>
          </div>
        </div>

        <!-- Scope Toggle -->
        <div class="pod-scope-toggle">
          <button class="pod-scope-btn ${this.currentScope === 'active' ? 'pod-scope-btn--active' : ''}" data-scope="active">
            未完成 <span class="count">${stats.activeTotal}</span>
          </button>
          <button class="pod-scope-btn ${this.currentScope === 'all' ? 'pod-scope-btn--active' : ''}" data-scope="all">
            全部 <span class="count">${stats.total}</span>
          </button>
        </div>

        <!-- Filters -->
        <div class="pod-internal-dashboard__filters">
          <button class="pod-filter-btn pod-filter-btn--active" data-filter="all">
            全部 <span class="count">${this.currentScope === 'active' ? stats.activeTotal : stats.total}</span>
          </button>
          <button class="pod-filter-btn" data-filter="awaiting-photo">
            待确认图片 <span class="count">${stats.awaitingPhoto}</span>
          </button>
          <button class="pod-filter-btn" data-filter="awaiting-art">
            待制作 <span class="count">${stats.awaitingArt}</span>
          </button>
          <button class="pod-filter-btn" data-filter="proof-sent">
            已发送校稿 <span class="count">${stats.proofSent}</span>
          </button>
          <button class="pod-filter-btn" data-filter="proof-approved">
            已确认 <span class="count">${stats.approved}</span>
          </button>
          <button class="pod-filter-btn" data-filter="proof-rejected">
            需修改 <span class="count">${stats.rejected}</span>
          </button>
          <button class="pod-filter-btn" data-filter="completed">
            已完成 <span class="count">${stats.completed}</span>
          </button>
          <input type="text" class="pod-search-input" placeholder="输入订单号、客户姓名或邮箱搜索…">
          <select class="pod-days-back-select" title="按创建时间筛选">
            <option value="1" ${this.daysBack === 1 ? 'selected' : ''}>今天</option>
            <option value="7" ${this.daysBack === 7 ? 'selected' : ''}>近 7 天</option>
            <option value="30" ${this.daysBack === 30 ? 'selected' : ''}>近 30 天</option>
            <option value="90" ${this.daysBack === 90 ? 'selected' : ''}>近 90 天</option>
            <option value="0" ${this.daysBack === 0 ? 'selected' : ''}>全部时间</option>
          </select>
          <select class="pod-sort-select">
            <option value="newest" ${this.currentSort === 'newest' ? 'selected' : ''}>最新优先</option>
            <option value="oldest" ${this.currentSort === 'oldest' ? 'selected' : ''}>最早优先</option>
            <option value="name-asc" ${this.currentSort === 'name-asc' ? 'selected' : ''}>订单号 A→Z</option>
            <option value="name-desc" ${this.currentSort === 'name-desc' ? 'selected' : ''}>订单号 Z→A</option>
          </select>
        </div>

        <!-- Orders Table -->
        ${filteredOrders.length > 0 ? this.renderOrdersTable(filteredOrders) : this.renderEmptyState()}
      </div>

      <!-- Modal (hidden by default) -->
      <div class="pod-modal-overlay" id="orderModal">
        <div class="pod-modal">
          <div class="pod-modal__header">
            <h2 class="pod-modal__title" id="modalTitle">订单详情</h2>
            <button class="pod-modal__close">&times;</button>
          </div>
          <div class="pod-modal__body" id="modalBody">
            <!-- Dynamic content -->
          </div>
          <div class="pod-modal__footer" id="modalFooter">
            <!-- Dynamic buttons -->
          </div>
        </div>
      </div>

      <!-- Toast Container -->
      <div class="pod-toast-container" id="toastContainer"></div>
    `;

    // 重新绑定上传事件
    this.bindUploadEvents();
    /* 重新渲染翻页栏 (render() 替换了 table, 旧的 tfoot 也没了) */
    this.renderPagination();
  }

  renderOrdersTable(orders) {
    return `
      <table class="pod-orders-table">
        <thead>
          <tr>
            <th>订单</th>
            <th>客户</th>
            <th>商品</th>
            <th>状态</th>
            <th>时间</th>
          </tr>
        </thead>
        <tbody>
          ${orders.map(order => `
            <tr data-order-id="${order.id}" ${order.isRush ? 'class="pod-order-row--rush"' : ''}>
              <td>
                <div class="pod-order-id">${order.name}</div>
                ${order.isRush ? '<span class="pod-rush-badge">⚡ 加急</span>' : ''}
              </td>
              <td>
                <div>${order.customer.name}</div>
                <div class="pod-order-customer">${order.customer.email}</div>
              </td>
              <td>${order.product}</td>
              <td>
                <span class="pod-status-badge pod-status-badge--${order.status}">
                  ${this.getStatusLabel(order.status)}
                </span>
              </td>
              <td>
                <div class="pod-order-time">${this.formatTime(order.created_at)}</div>
                ${order.status === 'proof-sent' ? `<div class="pod-order-time">${this.getTimeRemaining(order.proofSentAt)}</div>` : ''}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
  }

  renderOrders() {
    const tbody = this.container.querySelector('.pod-orders-table tbody');
    if (tbody) {
      const filteredOrders = this.getFilteredOrders();
      if (filteredOrders.length > 0) {
        tbody.innerHTML = filteredOrders.map(order => `
          <tr data-order-id="${order.id}" ${order.isRush ? 'class="pod-order-row--rush"' : ''}>
            <td>
              <div class="pod-order-id">${order.name}</div>
              ${order.isRush ? '<span class="pod-rush-badge">⚡ 加急</span>' : ''}
            </td>
            <td>
              <div>${order.customer.name}</div>
              <div class="pod-order-customer">${order.customer.email}</div>
            </td>
            <td>${order.product}</td>
            <td>
              <span class="pod-status-badge pod-status-badge--${order.status}">
                ${this.getStatusLabel(order.status)}
              </span>
            </td>
            <td>
              <div class="pod-order-time">${this.formatTime(order.created_at)}</div>
              ${order.status === 'proof-sent' ? `<div class="pod-order-time">${this.getTimeRemaining(order.proofSentAt)}</div>` : ''}
            </td>
          </tr>
        `).join('');
      } else {
        tbody.innerHTML = `
          <tr>
            <td colspan="5">
              ${this.renderEmptyState()}
            </td>
          </tr>
        `;
      }
    }
    this.renderPagination();
  }

  renderPagination() {
    const table = this.container.querySelector('.pod-orders-table');
    if (!table) return;
    let tfoot = table.querySelector('tfoot');
    const curPage = this.pageStack.length + 1;
    const prevDisabled = this.pageStack.length === 0;
    const nextDisabled = !this.hasNext || this.isLoadingPage;
    const html = `
      <tr>
        <td colspan="5">
          <div class="pod-pagination">
            <button class="pod-page-prev" ${prevDisabled ? 'disabled' : ''}>‹ 上一页</button>
            <span class="pod-page-info">第 ${curPage} 页${this.hasNext ? ' · 可加载更多' : ' · 已是最后一页'}</span>
            <button class="pod-page-next" ${nextDisabled ? 'disabled' : ''}>加载更多 ›</button>
          </div>
        </td>
      </tr>
    `;
    if (tfoot) {
      tfoot.innerHTML = html;
    } else {
      const newTfoot = document.createElement('tfoot');
      newTfoot.innerHTML = html;
      table.appendChild(newTfoot);
    }
  }

  renderEmptyState() {
    // 关键修复 (P0-5): 区分"无订单"和"加载错误"
    const errMsg = this._loadError;
    if (errMsg) {
      return `
        <div class="pod-empty-state">
          <div class="pod-empty-state__icon">⚠️</div>
          <div class="pod-empty-state__title">需要配置</div>
          <p>${errMsg}</p>
          <p style="font-size:12px;color:#999;margin-top:8px;">请在 Theme settings → POD 中配置 Worker endpoint，或检查 Worker 部署状态。</p>
        </div>
      `;
    }
    return `
      <div class="pod-empty-state">
        <div class="pod-empty-state__icon">📋</div>
        <div class="pod-empty-state__title">暂无订单</div>
        <p>尝试调整筛选条件或搜索关键词</p>
      </div>
    `;
  }

  updateFilterButtons() {
    const buttons = this.container.querySelectorAll('.pod-filter-btn');
    buttons.forEach(btn => {
      if (btn.dataset.filter === this.currentFilter) {
        btn.classList.add('pod-filter-btn--active');
      } else {
        btn.classList.remove('pod-filter-btn--active');
      }
    });
  }

  getStatusLabel(status) {
    const labels = {
      'awaiting-art': '待制作',
      'proof-sent': '已发送校稿',
      'proof-approved': '已确认',
      'proof-rejected': '需修改',
      'auto-approved': '自动确认',
      'in-production': '已完成'
    };
    return labels[status] || status;
  }

  openOrderModal(orderId) {
    this.selectedOrder = this.orders.find(o => o.id === orderId);
    if (!this.selectedOrder) return;

    const modal = this.container.querySelector('.pod-modal-overlay');
    const title = this.container.querySelector('#modalTitle');
    const body = this.container.querySelector('#modalBody');
    const footer = this.container.querySelector('#modalFooter');

    title.textContent = `订单 ${this.selectedOrder.name} - ${this.selectedOrder.customer.name}`;

    // 重置上传状态
    this.uploadedProofFile = null;
    this.uploadedProofUrl = null;

    // 根据状态渲染不同内容
    // awaiting-art 和 proof-rejected 都应该显示上传校稿组件（设计师需要重新上传）
    if (this.selectedOrder.status === 'awaiting-art' || this.selectedOrder.status === 'proof-rejected') {
      body.innerHTML = this.renderAwaitingArtContent();
      // 发送给客户按钮：必须文件已上传成功才可点击
      footer.innerHTML = `
        <button class="pod-btn pod-btn--secondary pod-cancel-btn">取消</button>
        <button class="pod-btn pod-btn--primary pod-send-proof-btn" disabled>
          发送给客户
        </button>
      `;
    } else {
      body.innerHTML = this.renderProofDetailContent();
      // 20260726-1 BUG-6 修复: proof-approved / auto-approved 状态显示"标记进入生产"按钮
      // 让设计师可以手动把订单状态从 approved 推到 in-production
      // (自动批准路径 handleAutoApproveProof 已自动推, 此按钮主要用于手动批准场景的兜底)
      const canPushToProduction = ['proof-approved', 'auto-approved'].includes(this.selectedOrder.status);
      footer.innerHTML = `
        ${canPushToProduction ? `
          <button class="pod-btn pod-btn--secondary pod-close-btn">关闭</button>
          <button class="pod-btn pod-btn--primary pod-push-production-btn">
            🚀 标记进入生产
          </button>
        ` : `
          <button class="pod-btn pod-btn--secondary pod-close-btn">关闭</button>
        `}
      `;
    }

    modal.classList.add('pod-modal-overlay--active');
    this.bindUploadEvents();

    // 从 localStorage 恢复已上传的校稿预览（跨 modal 关闭/打开持久化）
    var proofLsKey = 'pod_proof_' + this.selectedOrder.id;
    var savedProofData = null;
    try {
      var raw = localStorage.getItem(proofLsKey);
      if (raw) savedProofData = JSON.parse(raw);
    } catch (e) {}
    if (savedProofData && savedProofData.url) {
      requestAnimationFrame(() => {
        this.restoreProofPreview(savedProofData, true);
      });
    }
  }

  // 从 localStorage 恢复已上传的校稿预览
  // @param {string|Object} data - URL 字符串或 localStorage 解析出的 {url, key} 对象
  // @param {boolean} fromLocalStorage - 是否来自 localStorage
  restoreProofPreview(data, fromLocalStorage) {
    var existingUrl, r2Key;
    if (fromLocalStorage && typeof data === 'object' && data !== null) {
      existingUrl = data.url || '';
      r2Key = data.key || '';
    } else {
      existingUrl = (typeof data === 'string') ? data : '';
      r2Key = '';
    }
    if (!existingUrl) return;

    const modal = this.container.querySelector('.pod-modal');
    if (!modal) return;

    const uploadZone = modal.querySelector('.pod-upload-zone');
    const previewContainer = modal.querySelector('.pod-upload-preview-container');
    if (!previewContainer) return;

    // 如果来自 localStorage，说明是重新打开，无需再存一次；否则存入 localStorage
    if (!fromLocalStorage) {
      var orderId = this.selectedOrder ? this.selectedOrder.id : '';
      if (orderId) {
        try {
          localStorage.setItem('pod_proof_' + orderId, JSON.stringify({ url: existingUrl, key: r2Key }));
        } catch (e) {}
      }
    }

    // 恢复内存状态（供撤销删除时使用）
    this.uploadedProofUrl = existingUrl;
    if (r2Key) this.uploadedProofR2Key = r2Key;

    previewContainer.style.display = 'block';
    if (uploadZone) uploadZone.style.display = 'none';

    const imgEl = previewContainer.querySelector('[data-preview-img]');
    const videoEl = previewContainer.querySelector('[data-preview-video]');
    const isVideo = /\.(mp4|mov|webm|video\/|video\.)/i.test(existingUrl);

    if (isVideo) {
      if (imgEl) { imgEl.style.display = 'none'; imgEl.src = ''; }
      if (videoEl) { videoEl.style.display = 'block'; videoEl.src = existingUrl; }
    } else {
      if (videoEl) { videoEl.style.display = 'none'; videoEl.src = ''; }
      if (imgEl) { imgEl.style.display = 'block'; imgEl.src = existingUrl; imgEl.alt = '校稿预览'; }
    }

    const uploadBtn = previewContainer.querySelector('.pod-upload-now-btn');
    if (uploadBtn) { uploadBtn.textContent = '已上传 ✓'; uploadBtn.disabled = true; }

    // 启用"发送给客户"按钮
    const sendBtn = this.container.querySelector('.pod-send-proof-btn');
    if (sendBtn) { sendBtn.disabled = false; sendBtn.textContent = '发送给客户'; }
  }

  renderAwaitingArtContent() {
    const order = this.selectedOrder;
    return `
      <div class="pod-proof-detail">
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">客户照片</h3>
          <div class="pod-customer-images">
            ${order.image1 ? `
              <div class="pod-customer-image" data-pod-customer-image="Image 1">
                <img src="${order.image1}" alt="客户照片 1"
                  onerror="this.parentElement.classList.add('is-error')">
                <button type="button" class="pod-customer-image__remove"
                  data-pod-customer-image-remove
                  data-image-key="Image 1"
                  aria-label="删除照片 1">&times;</button>
                <div class="pod-customer-image__label">照片 1</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image1}" target="_blank"
                    title="${order.image1}">${order.image1}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image1}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image1}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image2 ? `
              <div class="pod-customer-image" data-pod-customer-image="Image 2">
                <img src="${order.image2}" alt="客户照片 2"
                  onerror="this.parentElement.classList.add('is-error')">
                <button type="button" class="pod-customer-image__remove"
                  data-pod-customer-image-remove
                  data-image-key="Image 2"
                  aria-label="删除照片 2">&times;</button>
                <div class="pod-customer-image__label">照片 2</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image2}" target="_blank"
                    title="${order.image2}">${order.image2}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image2}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image2}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image3 ? `
              <div class="pod-customer-image" data-pod-customer-image="Image 3">
                <img src="${order.image3}" alt="客户照片 3"
                  onerror="this.parentElement.classList.add('is-error')">
                <button type="button" class="pod-customer-image__remove"
                  data-pod-customer-image-remove
                  data-image-key="Image 3"
                  aria-label="删除照片 3">&times;</button>
                <div class="pod-customer-image__label">照片 3</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image3}" target="_blank"
                    title="${order.image3}">${order.image3}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image3}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image3}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image4 ? `
              <div class="pod-customer-image" data-pod-customer-image="Image 4">
                <img src="${order.image4}" alt="客户照片 4"
                  onerror="this.parentElement.classList.add('is-error')">
                <button type="button" class="pod-customer-image__remove"
                  data-pod-customer-image-remove
                  data-image-key="Image 4"
                  aria-label="删除照片 4">&times;</button>
                <div class="pod-customer-image__label">照片 4</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image4}" target="_blank"
                    title="${order.image4}">${order.image4}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image4}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image4}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image5 ? `
              <div class="pod-customer-image" data-pod-customer-image="Image 5">
                <img src="${order.image5}" alt="客户照片 5"
                  onerror="this.parentElement.classList.add('is-error')">
                <button type="button" class="pod-customer-image__remove"
                  data-pod-customer-image-remove
                  data-image-key="Image 5"
                  aria-label="删除照片 5">&times;</button>
                <div class="pod-customer-image__label">照片 5</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image5}" target="_blank"
                    title="${order.image5}">${order.image5}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image5}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image5}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${!order.image1 && !order.image2 && !order.image3 && !order.image4 && !order.image5 ? '<p style="color:#666;text-align:center;padding:2rem;">暂未上传照片</p>' : ''}
          </div>
        </div>
        ${order.proofImageUrl && order.status === 'proof-rejected' ? `
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">上次校稿（已被拒绝）</h3>
          <div class="pod-sent-proof-view">
            ${this.renderProofMedia(order.proofImageUrl)}
          </div>
        </div>
        ` : ''}
        ${(order.status === 'proof-rejected' && order.revisionFeedback) ? `
        <div class="pod-proof-detail__section pod-proof-detail__section--rejected">
          <h3 class="pod-proof-detail__section-title">修改记录</h3>
          <div class="pod-revision-record">
            <div class="pod-revision-record__header">
              <span class="pod-revision-badge">第 ${order.revisionCount} 次修改请求</span>
              ${order.proofRejectedAt ? `<span class="pod-revision-time">${this.formatTime(order.proofRejectedAt)}</span>` : ''}
            </div>
            <p class="pod-revision-record__feedback">${order.revisionFeedback}</p>
          </div>
        </div>
        ` : ''}
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">上传校稿${order.status === 'proof-rejected' ? '（重新上传）' : ''}</h3>
          <div class="pod-proof-upload">
            <div class="pod-upload-zone">
              <div class="pod-upload-zone__icon">📤</div>
              <div class="pod-upload-zone__text">点击或拖拽以上传校稿</div>
              <div class="pod-upload-zone__hint">点击或拖拽上传，支持 JPG、PNG、WebP、GIF、HEIC/HEIF、图片 PDF 或视频（MP4、MOV）（无大小限制）</div>
            </div>
            <input type="file" class="pod-upload-input" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf,video/mp4,video/quicktime,video/webm">
            <div class="pod-upload-preview-container" style="display:none;">
              <div class="pod-upload-preview">
                <img src="" alt="校稿预览" data-preview-img>
                <video src="" alt="校稿预览" data-preview-video style="display:none;" controls></video>
                <button class="pod-upload-preview__remove">&times;</button>
              </div>
              <button class="pod-upload-now-btn" disabled>上传到服务器</button>
            </div>
            <textarea class="pod-notes-input" placeholder="给客户的备注（可选）…"></textarea>
          </div>
        </div>
      </div>
    `;
  }

  renderProofDetailContent() {
    const order = this.selectedOrder;
    return `
      <div class="pod-proof-detail">
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">订单信息</h3>
          <div style="display:flex;flex-direction:column;gap:0.75rem;">
            <div><strong>客户：</strong> ${order.customer.name}</div>
            <div><strong>邮箱：</strong> ${order.customer.email}</div>
            <div><strong>商品：</strong> ${order.product}</div>
            <div><strong>状态：</strong> <span class="pod-status-badge pod-status-badge--${order.status}">${this.getStatusLabel(order.status)}</span></div>
            ${order.proofSentAt ? `<div><strong>校稿已发送：</strong> ${this.formatTime(order.proofSentAt)}</div>` : ''}
            ${order.status === 'proof-sent' ? `<div><strong>剩余时间：</strong> ${this.getTimeRemaining(order.proofSentAt)}</div>` : ''}
          </div>
        </div>
        ${(order.status === 'proof-rejected' && order.revisionFeedback) ? `
        <div class="pod-proof-detail__section pod-proof-detail__section--rejected">
          <h3 class="pod-proof-detail__section-title">修改记录</h3>
          <div class="pod-revision-record">
            <div class="pod-revision-record__header">
              <span class="pod-revision-badge">第 ${order.revisionCount} 次修改请求</span>
              ${order.proofRejectedAt ? `<span class="pod-revision-time">${this.formatTime(order.proofRejectedAt)}</span>` : ''}
            </div>
            <p class="pod-revision-record__feedback">${order.revisionFeedback}</p>
          </div>
        </div>
        ` : ''}
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">客户照片</h3>
          <div class="pod-customer-images">
            ${order.image1 ? `
              <div class="pod-customer-image">
                <img src="${order.image1}" alt="客户照片 1"
                  onerror="this.parentElement.classList.add('is-error')">
                <div class="pod-customer-image__label">照片 1</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image1}" target="_blank"
                    title="${order.image1}">${order.image1}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image1}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image1}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image2 ? `
              <div class="pod-customer-image">
                <img src="${order.image2}" alt="客户照片 2"
                  onerror="this.parentElement.classList.add('is-error')">
                <div class="pod-customer-image__label">照片 2</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image2}" target="_blank"
                    title="${order.image2}">${order.image2}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image2}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image2}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image3 ? `
              <div class="pod-customer-image">
                <img src="${order.image3}" alt="客户照片 3"
                  onerror="this.parentElement.classList.add('is-error')">
                <div class="pod-customer-image__label">照片 3</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image3}" target="_blank"
                    title="${order.image3}">${order.image3}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image3}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image3}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image4 ? `
              <div class="pod-customer-image">
                <img src="${order.image4}" alt="客户照片 4"
                  onerror="this.parentElement.classList.add('is-error')">
                <div class="pod-customer-image__label">照片 4</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image4}" target="_blank"
                    title="${order.image4}">${order.image4}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image4}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image4}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${order.image5 ? `
              <div class="pod-customer-image">
                <img src="${order.image5}" alt="客户照片 5"
                  onerror="this.parentElement.classList.add('is-error')">
                <div class="pod-customer-image__label">照片 5</div>
                <div class="pod-customer-image__url-bar">
                  <a class="pod-customer-image__url" href="${order.image5}" target="_blank"
                    title="${order.image5}">${order.image5}</a>
                  <button type="button" class="pod-btn pod-btn--xxs pod-btn--copy"
                    data-copy-url="${order.image5}"
                    title="复制链接">复制</button>
                  <a class="pod-btn pod-btn--xxs" href="${order.image5}" target="_blank"
                    title="在新标签页打开">下载</a>
                </div>
              </div>
            ` : ''}
            ${!order.image1 && !order.image2 && !order.image3 && !order.image4 && !order.image5 ? '<p style="color:#666;text-align:center;padding:2rem;">暂未上传照片</p>' : ''}
          </div>
        </div>
        ${order.proofImageUrl ? `
        <div class="pod-proof-detail__section">
          <h3 class="pod-proof-detail__section-title">校稿文件</h3>
          <div class="pod-sent-proof-view">
            ${this.renderProofMedia(order.proofImageUrl)}
          </div>
        </div>
        ` : ''}
      </div>
    `;
  }

  closeModal() {
    const modal = this.container.querySelector('.pod-modal-overlay');
    if (modal) {
      modal.classList.remove('pod-modal-overlay--active');
    }
    this.selectedOrder = null;
    this.uploadedProofFile = null;
    this.uploadedProofUrl = null;
  }

  // 20260726-1 BUG-6 修复: dashboard "标记进入生产" 按钮 handler
  // 调用 PODOrderAPI.updateOrder, 后端 handleOrderUpdate 推 pod_in_production event → F-009 → EM-006
  async pushOrderToProduction(btn) {
    if (!this.selectedOrder) return;

    const orderId = this.selectedOrder.id;
    const orderName = this.selectedOrder.name;

    if (!confirm(`确认把订单 ${orderName} 标记为进入生产？\n\n客户会立即收到 EM-006 "Order is Now in Production" 邮件。`)) {
      return;
    }

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = '🚀 标记中…';

    try {
      // PODOrderAPI.updateOrder 调用 /order/update 路由
      // pod-internal-api.js handleOrderUpdate 接收, 当 _proof_status=in-production 时
      // 推 pod_in_production event 给 Klaviyo (F-009 → EM-006)
      await PODOrderAPI.updateOrder(orderId, {
        '_proof_status': 'in-production',
        '_in_production_at': new Date().toISOString(),
      });
      this.showToast(`✓ 订单 ${orderName} 已标记进入生产, EM-006 邮件已触发`, 'success');

      // 关闭 modal 并刷新列表
      this.closeModal();
      if (this.resetPagination) this.resetPagination();
      this.refresh();
    } catch (e) {
      console.error('[pushOrderToProduction] failed:', e);
      this.showToast(`✗ 标记失败: ${e.message || '未知错误'}`, 'error');
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }

  // 两步流程第一步：上传文件到 R2
  async uploadProofNow() {
    if (!this.uploadedProofFile || !this.selectedOrder) {
      this.showToast('请先选择文件', 'error');
      return;
    }

    const modal = this.container.querySelector('.pod-modal');
    const uploadBtn = modal ? modal.querySelector('.pod-upload-now-btn') : null;
    const sendBtn = this.container.querySelector('.pod-send-proof-btn');

    if (uploadBtn) { uploadBtn.disabled = true; uploadBtn.textContent = '上传中…'; }

    try {
      if (!this.uploadEndpoint) {
        // Mock 模式
        this.uploadedProofUrl = await this.mockUpload(this.uploadedProofFile);
      } else {
        // Proof 图片放到 uploads/{YYYY-MM}/ORD-{orderId}/proofs/（与客户图同一订单文件夹下的 proofs 子文件夹）
        const result = await PODOrderAPI.uploadFile(this.uploadedProofFile, { orderId: this.selectedOrder.id, proof: true }, (pct) => {
          if (uploadBtn) uploadBtn.textContent = `上传中 ${pct}%…`;
        });
        this.uploadedProofUrl = result.publicUrl;
        this.uploadedProofR2Key = result.key || '';
      }

      // 上传成功后，启用"发送给客户"按钮
      if (sendBtn) {
        sendBtn.disabled = false;
        sendBtn.textContent = '发送给客户';
      }
      if (uploadBtn) {
        uploadBtn.textContent = '已上传 ✓';
        uploadBtn.disabled = true;
      }

      // 持久化到 localStorage（存 URL + R2 key，用于撤销删除）
      var orderId = this.selectedOrder ? this.selectedOrder.id : '';
      if (orderId && this.uploadedProofUrl) {
        try {
          localStorage.setItem('pod_proof_' + orderId, JSON.stringify({
            url: this.uploadedProofUrl,
            key: this.uploadedProofR2Key || ''
          }));
        } catch (e) {}
      }

      this.showToast('上传成功，现在可以发送给客户', 'success');
    } catch (error) {
      console.error('[POD Dashboard] Upload failed:', error);
      this.showToast('上传失败：' + error.message, 'error');
      if (uploadBtn) { uploadBtn.disabled = false; uploadBtn.textContent = '上传到服务器'; }
    }
  }

  async sendProof() {
    if ((!this.uploadedProofFile && !this.uploadedProofUrl) || !this.selectedOrder) {
      this.showToast('请先上传校稿图片', 'error');
      return;
    }

    const sendBtn = this.container.querySelector('.pod-send-proof-btn');
    const notesInput = this.container.querySelector('.pod-notes-input');
    const notes = notesInput ? notesInput.value : '';

    // 禁用按钮，显示加载状态
    sendBtn.disabled = true;
    sendBtn.textContent = '发送中…';

    try {
      let publicUrl = '';

      // 两步流程：已上传则复用 URL，否则内联上传
      if (this.uploadedProofUrl) {
        publicUrl = this.uploadedProofUrl;
      } else if (this.uploadEndpoint) {
        // 兼容：未走两步流程时，内联上传
        // Proof 图片放到 uploads/{YYYY-MM}/ORD-{orderId}/proofs/
        const result = await PODOrderAPI.uploadFile(this.uploadedProofFile, { orderId: this.selectedOrder.id, proof: true }, (pct) => {
          sendBtn.textContent = `上传中 ${pct}%…`;
        });
        publicUrl = result.publicUrl;
        // 保存 R2 key（用于撤销删除）
        if (result.key) this.uploadedProofR2Key = result.key;
      } else {
        // Mock 模式
        publicUrl = await this.mockUpload(this.uploadedProofFile);
      }

      // 调用 sendProof API 更新订单状态
      const file = this.uploadedProofFile;
      // 恢复预览时 uploadedProofFile 为 null，用 URL 和时间戳生成幂等键
      const idemKey = file
        ? `${this.selectedOrder.id}:${file.size}:${file.lastModified || 0}`
        : `${this.selectedOrder.id}:${publicUrl}:${Date.now()}`;
      await PODOrderAPI.sendProof(this.selectedOrder.id, publicUrl, notes, idemKey);

      console.log('[POD Dashboard] Proof sent:', {
        orderId: this.selectedOrder.id,
        publicUrl,
        notes
      });

      // 更新本地状态
      this.selectedOrder.status = 'proof-sent';
      this.selectedOrder.proofSentAt = new Date().toISOString();
      this.selectedOrder.proofImageUrl = publicUrl;

      // 发送成功后清除 localStorage（已持久化到 Shopify）
      var orderId = this.selectedOrder.id;
      try { localStorage.removeItem('pod_proof_' + orderId); } catch (e) {}

      this.showToast(`校稿已发送给 ${this.selectedOrder.customer.name}`, 'success');
      this.closeModal();
      this.render();
    } catch (error) {
      console.error('Failed to send proof:', error);
      this.showToast('发送校稿失败：' + error.message, 'error');
    } finally {
      if (sendBtn) {
        sendBtn.disabled = false;
        sendBtn.textContent = '发送给客户';
      }
    }
  }

  /**
   * Mock 上传（无 endpoint 时使用）
   */
  mockUpload(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        setTimeout(() => {
          resolve(reader.result); // 返回 base64 URL
        }, 500);
      };
      reader.readAsDataURL(file);
    });
  }

  handleRemoveCustomerImage(btn) {
    const order = this.selectedOrder;
    if (!order || !order.id) {
      this.showToast('未选择订单', 'error');
      return;
    }

    const imageKey = btn.getAttribute('data-image-key');
    if (!imageKey) return;

    if (!window.confirm(`删除订单 ${order.name} 的 ${imageKey}？此操作不可撤销。`)) {
      return;
    }

    const wrapper = btn.closest('[data-pod-customer-image]');
    if (wrapper) wrapper.classList.add('is-removing');
    btn.disabled = true;

    if (!window.PODOrderAPI || typeof window.PODOrderAPI.deleteOrderImage !== 'function') {
      this.showToast('删除图片 API 不可用，请刷新页面重试。', 'error');
      if (wrapper) wrapper.classList.remove('is-removing');
      btn.disabled = false;
      return;
    }

    const self = this;
    window.PODOrderAPI.deleteOrderImage(order.id, imageKey)
      .then(function (result) {
        if (!result || !result.success) {
          throw new Error((result && result.error) || '删除失败');
        }

        // 本地内存同步：被删的就清掉（Worker 已经重排过 Image N）
        if (imageKey === 'Image 1') order.image1 = null;
        if (imageKey === 'Image 2') order.image2 = null;

        // 从 DOM 移除该缩略图
        if (wrapper && wrapper.parentNode) {
          wrapper.parentNode.removeChild(wrapper);
        }

        // 如果全删了，刷新让 Liquid / 渲染逻辑自己回去
        if (!order.image1 && !order.image2) {
          self.showToast('所有客户照片已删除', 'success');
        } else {
          self.showToast(`${imageKey} 已删除`, 'success');
        }
      })
      .catch(function (err) {
        console.error('[Dashboard] Remove customer image failed:', err);
        if (wrapper) wrapper.classList.remove('is-removing');
        btn.disabled = false;
        self.showToast(`删除 ${imageKey} 失败：` + (err.message || '未知错误'), 'error');
      });
  }

  showToast(message, type = 'info') {
    const container = this.container.querySelector('#toastContainer') ||
                      document.createElement('div');

    if (!container.id) {
      container.id = 'toastContainer';
      container.className = 'pod-toast-container';
      document.body.appendChild(container);
    }

    const toast = document.createElement('div');
    toast.className = `pod-toast pod-toast--${type}`;
    toast.textContent = message;

    container.appendChild(toast);

    // 自动移除
    setTimeout(() => {
      toast.style.animation = 'slideIn 0.3s ease reverse';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// 初始化
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.pod-internal-dashboard-wrapper');
  if (container) {
    new PODInternalDashboard(container);
  }
});
